import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import {
  filesGenerateUploadUrl,
  filesGetFileUrl,
  filesSaveStorageId,
} from "~/api-refs";
import { convexClient } from "~/integrations/convex/provider";
import { parseStorageUploadResponse } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";

export function useFileUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [storageId, setStorageId] = useState<Id<"_storage"> | null>(null);

  const generateUploadUrlMutation = useMutation({
    mutationFn: () => {
      return convexClient.mutation(filesGenerateUploadUrl, {});
    },
  });

  const saveStorageIdMutation = useMutation({
    mutationFn: (data: { storageId: Id<"_storage"> }) => {
      return convexClient.mutation(filesSaveStorageId, data);
    },
  });

  // Get the file URL from the storage ID
  const { data: fileUrl } = useQuery(
    convexQuery(
      filesGetFileUrl,
      storageId
        ? {
            storageId,
          }
        : "skip"
    )
  );

  const uploadFile = async (file: File): Promise<Id<"_storage">> => {
    setIsUploading(true);
    try {
      // Step 1: Get a short-lived upload URL
      const postUrl = await generateUploadUrlMutation.mutateAsync();

      // Step 2: POST the file to the URL
      const result = await fetch(postUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!result.ok) {
        throw new Error(`Upload failed: ${result.statusText}`);
      }

      const uploadedStorageId = parseStorageUploadResponse(await result.json());

      // Step 3: Save the storage ID (optional verification step)
      await saveStorageIdMutation.mutateAsync({ storageId: uploadedStorageId });

      setStorageId(uploadedStorageId);
      return uploadedStorageId;
    } catch (error) {
      logger.exception("file_upload_failed", error);
      toast.error("Failed to upload file");
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadFile,
    isUploading,
    fileUrl,
  };
}

/**
 * Hook to get a file URL from a storage ID
 */
export function useFileUrl(storageId: Id<"_storage"> | null) {
  const { data } = useQuery(
    convexQuery(
      filesGetFileUrl,
      storageId
        ? {
            storageId,
          }
        : "skip"
    )
  );
  return data;
}
